import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, orderBy, where, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { loadAllUserBadges } from '../utils/badgeCalculator';
import UserStatsService from '../services/UserStatsService';

interface User {
  uid: string;
  name: string;
  nickname?: string;
  instrument?: string;
  totalPoints: number;
  weeklyPoints: number;
  monthlyPoints: number;
  practiceStreak: number;
  totalPracticeTime: number;
  weeklyPracticeTime: number;
  badges: string[];
  rank: number;
}

interface LeaderboardFilters {
  timePeriod: 'all' | 'weekly' | 'monthly';
  category: 'all' | 'students' | 'teachers';
  instrument: 'all' | string;
  sortBy: 'points' | 'streak' | 'practiceTime' | 'badges';
  limit: number;
}

const Leaderboard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<LeaderboardFilters>({
    timePeriod: 'all',
    category: 'all',
    instrument: 'all',
    sortBy: 'points',
    limit: 50
  });
  const [instruments, setInstruments] = useState<string[]>([]);
  const [currentUserRank, setCurrentUserRank] = useState<number | null>(null);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [highlightedUser, setHighlightedUser] = useState<string | null>(null);
  const userCardRef = useRef<HTMLDivElement>(null);

  // Use centralized service for user statistics
  const getUserStats = async (userId: string) => {
    const userStatsService = UserStatsService.getInstance();
    return await userStatsService.getUserStats(userId);
  };

  // Helper functions removed - now handled by UserStatsService

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      // Get all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const allUsers: User[] = [];
      const instrumentSet = new Set<string>();

      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        if (userData.role === 'student') {
          if (userData.instrument) {
            instrumentSet.add(userData.instrument);
          }

          // Get user's statistics from centralized service
          const userStats = await getUserStats(userDoc.id);

          allUsers.push({
            uid: userDoc.id,
            name: userData.name || 'Unknown',
            nickname: userData.nickname,
            instrument: userData.instrument,
            ...userStats,
            rank: 0 // Will be set after sorting
          });
        }
      }

      setInstruments(Array.from(instrumentSet).sort());

      // Sort users based on current filter
      let sortedUsers = [...allUsers];
      switch (filters.sortBy) {
        case 'points':
          sortedUsers.sort((a, b) => {
            const aPoints = filters.timePeriod === 'weekly' ? a.weeklyPoints : 
                           filters.timePeriod === 'monthly' ? a.monthlyPoints : a.totalPoints;
            const bPoints = filters.timePeriod === 'weekly' ? b.weeklyPoints : 
                           filters.timePeriod === 'monthly' ? b.monthlyPoints : b.totalPoints;
            return bPoints - aPoints;
          });
          break;
        case 'streak':
          sortedUsers.sort((a, b) => b.practiceStreak - a.practiceStreak);
          break;
        case 'practiceTime':
          sortedUsers.sort((a, b) => {
            const aTime = filters.timePeriod === 'weekly' ? a.weeklyPracticeTime : a.totalPracticeTime;
            const bTime = filters.timePeriod === 'weekly' ? b.weeklyPracticeTime : b.totalPracticeTime;
            return bTime - aTime;
          });
          break;
        case 'badges':
          sortedUsers.sort((a, b) => b.badges.length - a.badges.length);
          break;
      }

      // Apply filters
      let filteredUsers = sortedUsers;
      
      if (filters.instrument !== 'all') {
        filteredUsers = filteredUsers.filter(user => user.instrument === filters.instrument);
      }

      // Set ranks
      filteredUsers.forEach((user, index) => {
        user.rank = index + 1;
      });

      // Limit results
      filteredUsers = filteredUsers.slice(0, filters.limit);

      // Find current user's rank in the filtered results
      if (currentUser) {
        const currentUserIndex = filteredUsers.findIndex(user => user.uid === currentUser.uid);
        if (currentUserIndex !== -1) {
          setCurrentUserRank(currentUserIndex + 1);
        } else {
          // User not in filtered results, but show their overall rank
          const currentUserData = allUsers.find(user => user.uid === currentUser.uid);
          if (currentUserData) {
            const overallIndex = sortedUsers.findIndex(user => user.uid === currentUser.uid);
            setCurrentUserRank(overallIndex + 1);
          }
        }
      }

      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeaderboard();
  }, [filters, currentUser]);

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getPointsForPeriod = (user: User): number => {
    switch (filters.timePeriod) {
      case 'weekly': return user.weeklyPoints;
      case 'monthly': return user.monthlyPoints;
      default: return user.totalPoints;
    }
  };

  const getPracticeTimeForPeriod = (user: User): number => {
    switch (filters.timePeriod) {
      case 'weekly': return user.weeklyPracticeTime;
      default: return user.totalPracticeTime;
    }
  };

  // Find Me functionality
  const scrollToUser = () => {
    if (currentUser && userCardRef.current) {
      userCardRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      setHighlightedUser(currentUser.uid);
      // Remove highlight after 3 seconds
      setTimeout(() => setHighlightedUser(null), 3000);
    }
  };

  // Get rank emoji for mobile cards
  const getRankEmoji = (rank: number): string => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  // Get rank styling for mobile cards
  const getRankStyling = (rank: number) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-300';
    if (rank === 2) return 'bg-gradient-to-r from-gray-100 to-slate-100 border-gray-300';
    if (rank === 3) return 'bg-gradient-to-r from-orange-100 to-amber-100 border-orange-300';
    return 'bg-gradient-to-r from-indigo-100 to-purple-100 border-indigo-200';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-20 h-20 bg-yellow-300 rounded-full opacity-20 animate-bounce delay-1000"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-pink-300 rounded-full opacity-20 animate-bounce delay-2000"></div>
        <div className="absolute bottom-40 left-1/4 w-12 h-12 bg-blue-300 rounded-full opacity-20 animate-bounce delay-3000"></div>
        <div className="absolute bottom-20 right-1/3 w-14 h-14 bg-green-300 rounded-full opacity-20 animate-bounce delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-8 h-8 bg-purple-300 rounded-full opacity-20 animate-ping"></div>
      </div>

      <nav className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-2xl relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="text-4xl mr-3 animate-pulse">🏆</div>
              <h1 className="text-2xl font-bold text-white drop-shadow-lg">
                Practice Leaderboard
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {currentUser && (
                <div className="bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 border border-white/30">
                  <span className="text-sm text-white font-medium">
                    👋 {currentUser.nickname || currentUser.name}
                </span>
                </div>
              )}
              <button
                onClick={() => setShowPointsModal(true)}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 backdrop-blur-sm flex items-center hover:scale-105 shadow-lg"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                How to Earn Points
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 backdrop-blur-sm flex items-center hover:scale-105 shadow-lg"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Header - Only visible on mobile */}
      <div className="sm:hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-lg">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-all duration-200"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
              </button>
              <h2 className="text-lg font-bold text-white">🏆 Leaderboard</h2>
            </div>
            {currentUserRank && (
              <button
                onClick={scrollToUser}
                className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1"
              >
                <span>👤</span>
                <span>Find Me #{currentUserRank}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mobile Collapsible Filters */}
        {showMobileFilters && (
          <div className="sm:hidden bg-white rounded-lg shadow-lg p-4 mb-4 border border-gray-200">
            <div className="space-y-4">
              {/* Time Period Filter */}
              <div>
                <label className="block text-sm font-bold text-indigo-700 mb-2">📅 Time Period</label>
                <select
                  value={filters.timePeriod}
                  onChange={(e) => setFilters({...filters, timePeriod: e.target.value as any})}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="all">All Time</option>
                  <option value="weekly">This Week</option>
                  <option value="monthly">This Month</option>
                </select>
              </div>

              {/* Sort By Filter */}
              <div>
                <label className="block text-sm font-bold text-purple-700 mb-2">📊 Sort By</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => setFilters({...filters, sortBy: e.target.value as any})}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="points">Points</option>
                  <option value="streak">Practice Streak</option>
                  <option value="practiceTime">Practice Time</option>
                  <option value="badges">Badges</option>
                </select>
              </div>

              {/* Instrument Filter */}
              <div>
                <label className="block text-sm font-bold text-green-700 mb-2">🎵 Instrument</label>
                <select
                  value={filters.instrument}
                  onChange={(e) => setFilters({...filters, instrument: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="all">All Instruments</option>
                  {instruments.map(instrument => (
                    <option key={instrument} value={instrument}>{instrument}</option>
                  ))}
                </select>
              </div>

              {/* Results Limit */}
              <div>
                <label className="block text-sm font-bold text-orange-700 mb-2">📋 Show Top</label>
                <select
                  value={filters.limit}
                  onChange={(e) => setFilters({...filters, limit: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8 relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full opacity-10 animate-pulse"></div>
          </div>
          <div className="relative z-10">
            <h1 className="text-5xl sm:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 mb-4 drop-shadow-lg">
            🏆 Practice Leaderboard
          </h1>
            <div className="flex justify-center items-center space-x-2 mb-4">
              <span className="text-2xl animate-bounce">⭐</span>
              <span className="text-2xl animate-bounce delay-100">🎵</span>
              <span className="text-2xl animate-bounce delay-200">🔥</span>
              <span className="text-2xl animate-bounce delay-300">🏅</span>
              <span className="text-2xl animate-bounce delay-400">⭐</span>
            </div>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto font-medium leading-relaxed">
            See how you rank among your peers! Points are earned through consistent practice, 
            skill development, and engagement with your musical journey.
          </p>
            <div className="mt-6 flex justify-center space-x-4">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full font-bold shadow-lg animate-pulse">
                🎯 Compete & Win!
              </div>
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full font-bold shadow-lg animate-pulse delay-500">
                🚀 Level Up!
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Filters - Hidden on mobile */}
        <div className="hidden sm:block bg-gradient-to-br from-white via-indigo-50 to-purple-50 rounded-2xl shadow-2xl p-8 mb-8 border-2 border-indigo-200 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full opacity-10 transform translate-x-10 -translate-y-10"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-br from-pink-400 to-purple-400 rounded-full opacity-10 transform -translate-x-8 translate-y-8"></div>
          <div className="relative z-10">
            <h2 className="text-2xl font-bold text-center mb-6 text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
              🎛️ Filter & Sort
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Time Period Filter */}
            <div className="transform hover:scale-105 transition-all duration-300">
              <label className="block text-sm font-bold text-indigo-700 mb-3 flex items-center">
                <span className="mr-2 text-lg">📅</span>
                Time Period
              </label>
              <select
                value={filters.timePeriod}
                onChange={(e) => setFilters({...filters, timePeriod: e.target.value as any})}
                className="w-full px-4 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-400 text-gray-800 font-bold transition-all duration-300 hover:border-blue-300 hover:shadow-lg transform hover:scale-105"
              >
                <option value="all">All Time</option>
                <option value="weekly">This Week</option>
                <option value="monthly">This Month</option>
              </select>
            </div>

            {/* Sort By Filter */}
            <div className="transform hover:scale-105 transition-all duration-300">
              <label className="block text-sm font-bold text-purple-700 mb-3 flex items-center">
                <span className="mr-2 text-lg">📊</span>
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters({...filters, sortBy: e.target.value as any})}
                className="w-full px-4 py-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-400 text-gray-800 font-bold transition-all duration-300 hover:border-purple-300 hover:shadow-lg transform hover:scale-105"
              >
                <option value="points">Points</option>
                <option value="streak">Practice Streak</option>
                <option value="practiceTime">Practice Time</option>
                <option value="badges">Badges</option>
              </select>
            </div>

            {/* Instrument Filter */}
            <div className="transform hover:scale-105 transition-all duration-300">
              <label className="block text-sm font-bold text-green-700 mb-3 flex items-center">
                <span className="mr-2 text-lg">🎵</span>
                Instrument
              </label>
              <select
                value={filters.instrument}
                onChange={(e) => setFilters({...filters, instrument: e.target.value})}
                className="w-full px-4 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-300 focus:border-green-400 text-gray-800 font-bold transition-all duration-300 hover:border-green-300 hover:shadow-lg transform hover:scale-105"
              >
                <option value="all">All Instruments</option>
                {instruments.map(instrument => (
                  <option key={instrument} value={instrument}>{instrument}</option>
                ))}
              </select>
            </div>

            {/* Results Limit */}
            <div className="transform hover:scale-105 transition-all duration-300">
              <label className="block text-sm font-bold text-orange-700 mb-3 flex items-center">
                <span className="mr-2 text-lg">📋</span>
                Show Top
              </label>
              <select
                value={filters.limit}
                onChange={(e) => setFilters({...filters, limit: parseInt(e.target.value)})}
                className="w-full px-4 py-4 bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-300 focus:border-orange-400 text-gray-800 font-bold transition-all duration-300 hover:border-orange-300 hover:shadow-lg transform hover:scale-105"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>

            {/* Current User Rank */}
            {currentUserRank && (
              <div className="flex items-center">
                {currentUserRank <= 3 ? (
                  // Celebratory styling for top 3 ranks
                  <div className={`relative overflow-hidden rounded-2xl p-4 text-center shadow-xl transform hover:scale-105 transition-all duration-300 ${
                    currentUserRank === 1 
                      ? 'bg-gradient-to-br from-yellow-100 via-amber-50 to-orange-100 border-2 border-yellow-400 animate-pulse' 
                      : currentUserRank === 2 
                      ? 'bg-gradient-to-br from-gray-100 via-slate-50 to-zinc-100 border-2 border-gray-400' 
                      : 'bg-gradient-to-br from-orange-100 via-amber-50 to-yellow-100 border-2 border-orange-400'
                  }`}>
                    {/* Celebration sparkles for rank 1 */}
                    {currentUserRank === 1 && (
                      <>
                        <div className="absolute top-1 left-1 text-yellow-400 text-sm animate-bounce">✨</div>
                        <div className="absolute top-2 right-2 text-yellow-400 text-xs animate-bounce delay-100">⭐</div>
                        <div className="absolute bottom-1 left-2 text-yellow-400 text-xs animate-bounce delay-200">💫</div>
                        <div className="absolute bottom-1 right-1 text-yellow-400 text-sm animate-bounce delay-300">✨</div>
                      </>
                    )}
                    
                    <div className="relative z-10">
                      <div className="text-3xl mb-1 animate-bounce">
                        {currentUserRank === 1 ? '🥇' : currentUserRank === 2 ? '🥈' : '🥉'}
                      </div>
                      <div className={`text-2xl font-black mb-1 ${
                        currentUserRank === 1 
                          ? 'bg-gradient-to-r from-yellow-600 to-amber-600 bg-clip-text text-transparent' 
                          : currentUserRank === 2 
                          ? 'bg-gradient-to-r from-gray-600 to-slate-600 bg-clip-text text-transparent' 
                          : 'bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent'
                      }`}>
                        #{currentUserRank}
                      </div>
                      <div className={`text-xs font-bold uppercase tracking-wider ${
                        currentUserRank === 1 ? 'text-yellow-800' : currentUserRank === 2 ? 'text-gray-800' : 'text-orange-800'
                      }`}>
                        {currentUserRank === 1 ? '🏆 CHAMPION!' : currentUserRank === 2 ? '🥈 RUNNER-UP!' : '🥉 TOP THREE!'}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {filters.instrument !== 'all' ? `Among ${filters.instrument} players` : 
                         filters.timePeriod !== 'all' ? `This ${filters.timePeriod}` : 
                         'Overall'}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Regular styling for other ranks
                  <div className="bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl p-4 text-center border-2 border-indigo-200 shadow-xl transform hover:scale-105 transition-all duration-300">
                    <div className="text-2xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-1">
                      #{currentUserRank}
                    </div>
                    <div className="text-xs font-bold text-indigo-700 uppercase tracking-wider">
                      Your Rank
                    </div>
                    <div className="text-xs text-indigo-600 mt-1">
                      Keep practicing! 🚀
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {filters.instrument !== 'all' ? `Among ${filters.instrument} players` : 
                       filters.timePeriod !== 'all' ? `This ${filters.timePeriod}` : 
                       'Overall'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Mobile Card Layout */}
        <div className="sm:hidden space-y-3 mb-6">
          {loading ? (
            <div className="p-8 text-center">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 mx-auto"></div>
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mx-auto absolute top-0 left-1/2 transform -translate-x-1/2"></div>
              </div>
              <p className="mt-4 text-lg text-gray-600 font-bold">Loading leaderboard...</p>
            </div>
          ) : (
            users.map((user, index) => (
              <div
                key={user.uid}
                ref={currentUser && user.uid === currentUser.uid ? userCardRef : null}
                className={`bg-white rounded-lg shadow-lg p-4 border-2 transition-all duration-300 ${
                  getRankStyling(user.rank)
                } ${
                  highlightedUser === user.uid ? 'animate-pulse ring-4 ring-indigo-300' : ''
                } ${
                  currentUser && user.uid === currentUser.uid ? 'ring-2 ring-indigo-400' : ''
                }`}
              >
                {/* Top Row - Rank and Name */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="text-2xl">
                      {getRankEmoji(user.rank)}
                    </div>
                    <div className="font-bold text-gray-900 text-lg">
                      {user.nickname || user.name}
                    </div>
                  </div>
                  {currentUser && user.uid === currentUser.uid && (
                    <div className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs font-bold">
                      YOU
                    </div>
                  )}
                </div>

                {/* Second Row - Instrument and Points */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-1">
                    <span className="text-sm">🎵</span>
                    <span className="text-sm font-medium text-gray-700">
                      {user.instrument || 'Unknown'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-sm">⭐</span>
                    <span className="text-sm font-bold text-green-600">
                      {getPointsForPeriod(user).toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Third Row - Streak and Badges */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1">
                    <span className="text-sm">🔥</span>
                    <span className="text-sm font-medium text-orange-600">
                      {user.practiceStreak} days
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-sm">🏅</span>
                    <span className="text-sm font-medium text-yellow-600">
                      {user.badges.length} badges
                    </span>
                  </div>
                </div>

                {/* Fourth Row - Practice Time */}
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="flex items-center justify-center space-x-1">
                    <span className="text-sm">⏰</span>
                    <span className="text-sm font-medium text-gray-600">
                      {formatTime(getPracticeTimeForPeriod(user))} this {filters.timePeriod === 'weekly' ? 'week' : filters.timePeriod === 'monthly' ? 'month' : 'total'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table - Hidden on mobile */}
        <div className="hidden sm:block bg-white shadow-2xl rounded-2xl overflow-hidden border-2 border-indigo-200 relative">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
          <div className="absolute top-4 right-4 text-2xl animate-bounce">🏆</div>
          <div className="absolute top-4 left-4 text-2xl animate-bounce delay-1000">⭐</div>
          
          {loading ? (
            <div className="p-12 text-center">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 mx-auto"></div>
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-600 border-t-transparent mx-auto absolute top-0 left-1/2 transform -translate-x-1/2"></div>
              </div>
              <p className="mt-6 text-xl text-gray-600 font-bold">Loading leaderboard...</p>
              <div className="flex justify-center space-x-1 mt-4">
                <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce delay-100"></div>
                <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce delay-200"></div>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100">
                  <tr>
                    <th className="px-6 py-6 text-center text-lg font-black text-indigo-800 uppercase tracking-wider">
                      🏆 Rank
                    </th>
                    <th className="px-6 py-6 text-center text-lg font-black text-indigo-800 uppercase tracking-wider">
                      👤 Student
                    </th>
                    <th className="px-6 py-6 text-center text-lg font-black text-indigo-800 uppercase tracking-wider">
                      🎵 Instrument
                    </th>
                    <th className="px-6 py-6 text-center text-lg font-black text-indigo-800 uppercase tracking-wider">
                      ⭐ Points
                    </th>
                    <th className="px-6 py-6 text-center text-lg font-black text-indigo-800 uppercase tracking-wider">
                      ⏰ Practice Time
                    </th>
                    <th className="px-6 py-6 text-center text-lg font-black text-indigo-800 uppercase tracking-wider">
                      🔥 Streak
                    </th>
                    <th className="px-6 py-6 text-center text-lg font-black text-indigo-800 uppercase tracking-wider">
                      🏅 Badges
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gradient-to-b from-white to-gray-50">
                  {users.map((user, index) => {
                    // Determine rank-based styling
                    let rankBorderClass = '';
                    let rankCellClass = '';
                    let rankGlowClass = '';
                    let rankAnimationClass = '';
                    
                    if (index === 0) {
                      rankBorderClass = 'border-4 border-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-50 shadow-2xl rounded-2xl';
                      rankCellClass = 'py-8';
                      rankGlowClass = 'shadow-yellow-200';
                      rankAnimationClass = 'animate-pulse';
                    } else if (index === 1) {
                      rankBorderClass = 'border-4 border-gray-400 bg-gradient-to-r from-gray-50 to-slate-50 shadow-2xl rounded-2xl';
                      rankCellClass = 'py-8';
                      rankGlowClass = 'shadow-gray-200';
                    } else if (index === 2) {
                      rankBorderClass = 'border-4 border-orange-400 bg-gradient-to-r from-orange-50 to-amber-50 shadow-2xl rounded-2xl';
                      rankCellClass = 'py-8';
                      rankGlowClass = 'shadow-orange-200';
                    }
                    
                    const elements = [];
                    
                    // Add spacing row before rank 2, 3, and 4
                    if (index === 1 || index === 2 || index === 3) {
                      elements.push(
                        <tr key={`spacer-${index}`} className="h-4">
                          <td colSpan={7} className="p-0"></td>
                        </tr>
                      );
                    }
                    
                    // Add the main row
                    elements.push(
                    <tr 
                      key={user.uid} 
                      className={`transition-all duration-500 transform hover:scale-105 ${
                        index < 3 ? rankBorderClass : 'hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:shadow-lg'
                      } ${
                        currentUser && user.uid === currentUser.uid && index >= 3 ? 'bg-gradient-to-r from-indigo-100 to-purple-100 border-l-4 border-indigo-500 shadow-lg' : ''
                      } ${rankAnimationClass}`}
                    >
                      <td className={`px-6 whitespace-nowrap text-center ${index < 3 ? rankCellClass : 'py-6'}`}>
                        <div className="flex items-center justify-center">
                          {index < 3 ? (
                            <div className="relative">
                              <span className="text-6xl animate-bounce">
                                {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                              </span>
                              {index === 0 && (
                                <>
                                  <div className="absolute -top-2 -right-2 text-yellow-400 text-lg animate-ping">✨</div>
                                  <div className="absolute -bottom-2 -left-2 text-yellow-400 text-sm animate-ping delay-500">⭐</div>
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 border-2 border-indigo-200 shadow-lg">
                              <span className="text-lg font-black text-indigo-700">
                                #{user.rank}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className={`px-6 whitespace-nowrap text-center ${index < 3 ? rankCellClass : 'py-6'}`}>
                        <div className="flex items-center justify-center">
                          <div className="text-lg font-bold text-gray-900">
                            {user.nickname || user.name}
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 whitespace-nowrap text-center ${index < 3 ? rankCellClass : 'py-6'}`}>
                        <div className="flex items-center justify-center">
                          <span className="inline-flex px-4 py-2 text-sm font-bold bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 rounded-full border-2 border-purple-200 shadow-lg">
                            🎵 {user.instrument || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className={`px-6 whitespace-nowrap text-center ${index < 3 ? rankCellClass : 'py-6'}`}>
                        <div className="flex items-center justify-center">
                          <span className="text-2xl font-black bg-gradient-to-r from-green-500 to-emerald-600 bg-clip-text text-transparent">
                            ⭐ {getPointsForPeriod(user).toLocaleString()}
                          </span>
                        </div>
                      </td>
                      <td className={`px-6 whitespace-nowrap text-center text-lg text-gray-700 font-bold ${index < 3 ? rankCellClass : 'py-6'}`}>
                        ⏰ {formatTime(getPracticeTimeForPeriod(user))}
                      </td>
                      <td className={`px-6 whitespace-nowrap text-center ${index < 3 ? rankCellClass : 'py-6'}`}>
                        <div className="flex items-center justify-center">
                          <span className="inline-flex px-4 py-2 text-sm font-bold bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 rounded-full border-2 border-orange-200 shadow-lg">
                            🔥 {user.practiceStreak} days
                          </span>
                        </div>
                      </td>
                      <td className={`px-6 whitespace-nowrap text-center ${index < 3 ? rankCellClass : 'py-6'}`}>
                        <div className="flex items-center justify-center">
                          <span className="inline-flex px-4 py-2 text-sm font-bold bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 rounded-full border-2 border-yellow-200 shadow-lg">
                            🏅 {user.badges.length} badges
                          </span>
                        </div>
                      </td>
                    </tr>
                    );
                    
                    return elements;
                  }).flat()}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Motivational Footer */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-8 shadow-2xl">
            <h3 className="text-3xl font-bold text-white mb-4">
              🎯 Ready to Climb the Leaderboard?
            </h3>
            <p className="text-xl text-white/90 mb-6">
              Practice daily, earn points, and watch yourself rise to the top!
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate('/practice-tasks')}
                className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                🎵 Start Practicing
              </button>
              <button
                onClick={() => navigate('/record-lesson')}
                className="bg-white/20 text-white border-2 border-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-indigo-600 transition-all duration-300 transform hover:scale-105"
              >
                🎙️ Record Lesson
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Points System Modal */}
      {showPointsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border-4 border-indigo-200 relative">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-t-3xl"></div>
            <div className="absolute top-4 right-4 text-2xl animate-bounce">⭐</div>
            <div className="absolute top-4 left-4 text-2xl animate-bounce delay-1000">🏆</div>
            
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-t-3xl px-8 py-6 flex justify-between items-center shadow-lg">
              <h2 className="text-3xl font-black text-white flex items-center">
                <span className="mr-4 text-4xl">📊</span>
                How to Earn Points
              </h2>
              <button
                onClick={() => setShowPointsModal(false)}
                className="text-white hover:text-gray-200 transition-colors bg-white/20 hover:bg-white/30 rounded-full p-2"
              >
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-8">
              <div className="mb-10 text-center">
                <div className="flex justify-center space-x-2 mb-4">
                  <span className="text-3xl animate-bounce">🎯</span>
                  <span className="text-3xl animate-bounce delay-100">⭐</span>
                  <span className="text-3xl animate-bounce delay-200">🏆</span>
                  <span className="text-3xl animate-bounce delay-300">🎵</span>
                  <span className="text-3xl animate-bounce delay-400">🔥</span>
                </div>
                <p className="text-2xl text-gray-700 font-bold mb-4">
                  Points are your way to track progress and compete with other students!
                </p>
                <p className="text-lg text-gray-600">
                  Here's everything you need to know about earning points:
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Practice Points */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center">
                    <span className="mr-2">🎯</span>
                    Practice Points
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="w-3 h-3 bg-green-500 rounded-full mr-3 mt-1"></span>
                      <div>
                        <p className="font-semibold text-green-800">Complete Daily Goal</p>
                        <p className="text-green-700">+10 points when you practice for your full daily goal</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="w-3 h-3 bg-yellow-500 rounded-full mr-3 mt-1"></span>
                      <div>
                        <p className="font-semibold text-green-800">Half Daily Goal</p>
                        <p className="text-green-700">+5 points when you practice for 50% of your daily goal</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="w-3 h-3 bg-blue-500 rounded-full mr-3 mt-1"></span>
                      <div>
                        <p className="font-semibold text-green-800">Guided Practice</p>
                        <p className="text-green-700">+5 points for structured lesson practice sessions</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="w-3 h-3 bg-purple-500 rounded-full mr-3 mt-1"></span>
                      <div>
                        <p className="font-semibold text-green-800">Free Practice</p>
                        <p className="text-green-700">+3 points for independent practice sessions</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="w-3 h-3 bg-pink-500 rounded-full mr-3 mt-1"></span>
                      <div>
                        <p className="font-semibold text-green-800">Multi-Category Practice</p>
                        <p className="text-green-700">+5 points when you practice 3+ different categories</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bonus Points */}
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6 border border-orange-200">
                  <h3 className="text-xl font-bold text-orange-800 mb-4 flex items-center">
                    <span className="mr-2">⭐</span>
                    Bonus Points
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="w-3 h-3 bg-orange-500 rounded-full mr-3 mt-1"></span>
                      <div>
                        <p className="font-semibold text-orange-800">Weekly Frequency</p>
                        <p className="text-orange-700">+5 to +20 points for meeting weekly session goals</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="w-3 h-3 bg-yellow-500 rounded-full mr-3 mt-1"></span>
                      <div>
                        <p className="font-semibold text-orange-800">Badges Earned</p>
                        <p className="text-orange-700">+5 points for each badge your teacher awards you</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="w-3 h-3 bg-red-500 rounded-full mr-3 mt-1"></span>
                      <div>
                        <p className="font-semibold text-orange-800">Streak Multipliers</p>
                        <p className="text-orange-700">1.2x to 2.0x multiplier based on practice streak</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Streak Multipliers Detail */}
              <div className="mt-8 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                <h3 className="text-xl font-bold text-indigo-800 mb-4 flex items-center">
                  <span className="mr-2">🔥</span>
                  Practice Streaks
                </h3>
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center mb-2">
                    <span className="mr-2">⏰</span>
                    <span className="font-semibold text-yellow-800">Streak Requirements</span>
                  </div>
                  <p className="text-yellow-700 text-sm">
                    Each practice session must be <strong>at least 10 minutes long</strong> to count toward your streak. 
                    You need to practice for 10+ minutes on consecutive days to build your streak!
                  </p>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white/60 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600 mb-2">7+ Days</div>
                    <div className="text-sm text-gray-700">1.2x Multiplier</div>
                  </div>
                  <div className="bg-white/60 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-red-600 mb-2">30+ Days</div>
                    <div className="text-sm text-gray-700">1.5x Multiplier</div>
                  </div>
                  <div className="bg-white/60 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600 mb-2">100+ Days</div>
                    <div className="text-sm text-gray-700">2.0x Multiplier</div>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="mt-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center">
                  <span className="mr-2">💡</span>
                  Pro Tips
                </h3>
                <ul className="space-y-2 text-blue-700">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Practice for at least 10 minutes daily to build your streak multiplier</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Consistency beats intensity - daily practice builds your streak multiplier</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Mix different practice categories to earn multi-category bonus points</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Work with your teacher to earn badges for extra points</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Even short practice sessions count toward your weekly goals</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;

